import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, signal } from '@angular/core';
import * as presenter from './dashboard-comissoes.presenter';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Toast } from '@/shared/components/toast/toast';
import { TablePageEvent } from 'primeng/table';
import { Observable, Subscription, catchError, forkJoin, map, of, switchMap } from 'rxjs';

import {
    ComissaoAtaModeloResponse,
    ComissaoAtaParticipanteRequest,
    ComissaoAtaConvidadoResponse,
    ComissaoAtaFinalizacaoTextoResponse,
    ComissaoAtaInicioTextoResponse,
    ComissaoAtaProtocoloResponse,
    ComissaoEstruturaFiltroResponse,
    ComissaoLocalReuniaoResponse,
    ComissaoReuniaoFiltroRequest,
    ComissaoReuniaoMembroResponse,
    ComissaoReuniaoResponse,
    ComissaoReuniaoSalvarRequest,
    ComissaoReuniaoSalvarResponse,
    EComissaoReuniaoTipoPeriodoFiltro,
    EComissaoReuniaoTipoConvocacaoFiltro,
    EComissaoReuniaoMarcacaoAtaFiltro,
    ComissaoStatusAtaResponse,
    ComissaoTipoComissaoResponse,
    ComissaoReuniaoDashboardSummaryResponse
} from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { ComissaoReuniaoService } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.service';
import { ComissaoCadastroService } from '@/core/api/sicom-api/comissoes/cadastros/comissao-cadastro.service';
import { CctMembroResponse } from '@/core/api/sicom-api/comissoes/cadastros/comissao-cadastro.model';
import { ComissaoAtividadeResponse, ComissaoAtividadeSalvarRequest, ComissaoSituacaoAtividadeResponse, EComissaoAtividadeTipoPesquisaFiltro } from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.model';
import { ComissaoAtividadeService } from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.service';
import { AtaReuniaoComponent } from '@/features/comissoes/dashboard-comissoes/components/ata-reuniao/ata-reuniao';
import { DashboardAtividadesComissoesComponent } from '@/features/comissoes/dashboard-comissoes/components/dashboard-atividades-comissoes/dashboard-atividades-comissoes';
import { FormularioReuniaoComissoesComponent } from '@/features/comissoes/dashboard-comissoes/components/formulario-reuniao-comissoes/formulario-reuniao-comissoes';
import { GerenciarConvocacoesComponent } from '@/features/comissoes/dashboard-comissoes/components/gerenciar-convocacoes/gerenciar-convocacoes';
import { FiltrosComissoesComponent } from '@/features/comissoes/dashboard-comissoes/components/filtros-comissoes/filtros-comissoes';
import { AcaoTabelaComissoes, TabelaReunioesComissoesComponent } from '@/features/comissoes/dashboard-comissoes/components/tabela-reunioes-comissoes/tabela-reunioes-comissoes';
import {
    AtividadeAta,
    FiltrosComissoes,
    OpcaoFiltroComissao,
    ParticipanteAta,
    PeriodoFiltroComissao,
    ReuniaoComissao,
    ReuniaoSalvarPayload,
    TipoComissaoPessoa,
    criarFiltrosComissoesIniciais
} from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type AcaoComissoes = 'cadastrar' | 'cancelar' | 'convocacoes' | 'ata' | 'relatorio' | 'visualizar';
type IdentificacaoParticipanteAta = { idSolicitante: number; uf: string };
const ID_ESTRUTURA_TODAS_COMISSOES = 0;

@Component({
    selector: 'app-dashboard-comissoes',
    standalone: true,
    imports: [
        CommonModule,
        ConfirmDialogModule,
        FiltrosComissoesComponent,
        TabelaReunioesComissoesComponent,
        DashboardAtividadesComissoesComponent,
        AtaReuniaoComponent,
        FormularioReuniaoComissoesComponent,
        GerenciarConvocacoesComponent,
        Toast
    ],
    providers: [ConfirmationService, MessageService],
    host: { '(window:resize)': 'aoRedimensionarJanela()' },
    templateUrl: './dashboard-comissoes.html',
    styleUrls: ['./dashboard-comissoes.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComissoesComponent implements OnInit, OnDestroy {
    tipo: TipoComissaoPessoa = 'acompanhamento';
    private salvandoManual = false;
    private telaEstreitaFiltros = typeof window !== 'undefined' && window.innerWidth <= 1200;
    /**
     * Os filtros começam ocultos: a tela abre direto na lista, e a coluna lateral só entra
     * quando o usuário pede pelo botão de filtro. O estado é local e não persiste entre
     * navegações.
     */
    readonly filtrosVisiveis = signal(false);
    carregandoReunioes = false;
    dashboardSummary: ComissaoReuniaoDashboardSummaryResponse | null = null;

    private _erroConsulta = '';
    get erroConsulta(): string {
        return this._erroConsulta;
    }
    set erroConsulta(value: string) {
        this._erroConsulta = value;
        if (value) {
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: value });
        }
    }

    filtrosAtivos: FiltrosComissoes = criarFiltrosComissoesIniciais();
    opcoesLocais: OpcaoFiltroComissao[] = [{ rotulo: 'Todos', valor: 'todos' }];
    locaisReuniao: ComissaoLocalReuniaoResponse[] = [];
    opcoesAssistentes: OpcaoFiltroComissao[] = [{ rotulo: 'TODOS', valor: 'todos' }];
    opcoesStatusAta: OpcaoFiltroComissao[] = [{ rotulo: 'Todos', valor: 'todos' }];
    tiposComissao: ComissaoTipoComissaoResponse[] = [];
    estruturasComissao: ComissaoEstruturaFiltroResponse[] = [];
    situacoesAtividade: ComissaoSituacaoAtividadeResponse[] = [];
    carregandoEstruturasComissao = false;
    erroEstruturasComissao = '';

    reuniaoSelecionada: ReuniaoComissao | null = null;
    reunioesPorTipo: ReuniaoComissao[] = [];
    reunioesFiltradas: ReuniaoComissao[] = [];
    totalRegistrosReunioes = 0;
    primeiraLinhaReunioes = 0;
    linhasPorPaginaReunioes = 20;

    private _ultimaAcaoExecutada = '';
    get ultimaAcaoExecutada(): string {
        return this._ultimaAcaoExecutada;
    }
    set ultimaAcaoExecutada(value: string) {
        this._ultimaAcaoExecutada = value;
        if (value) {
            const isError = value.toLowerCase().includes('nao') || value.toLowerCase().includes('não') || value.toLowerCase().includes('erro');
            this.messageService.add({
                severity: isError ? 'error' : 'success',
                summary: isError ? 'Erro' : 'Sucesso',
                detail: value
            });
        }
    }

    telaAtaVisivel = false;
    documentoAtaHtml = '';
    etagAtaAtual = '';
    autosavePausadoAta = false;
    conflitoAtaMensagem = '';
    textoInicioAtaModelo = '';
    gatilhoInsercaoInicioAta = 0;
    textoFinalizacaoAtaModelo = '';
    gatilhoInsercaoFinalizacaoAta = 0;
    membrosAta: ParticipanteAta[] = [];
    convidadosAta: ParticipanteAta[] = [];
    atividadesAta: AtividadeAta[] = [];
    modelosAta: ComissaoAtaModeloResponse[] = [];
    autosaveIntervaloSegundos = 0;
    formularioReuniaoVisivel = false;
    reuniaoFormulario: ReuniaoComissao | null = null;
    gerenciarConvocacoesVisivel = false;

    private paramsSub?: Subscription;
    private codigoRotaSub?: Subscription;
    private consultaReunioesSub?: Subscription;
    private consultaDashboardSummarySub?: Subscription;
    private consultaStatusAtaSub?: Subscription;
    private consultaLocaisSub?: Subscription;
    private consultaAssistentesSub?: Subscription;
    private consultaEstruturasComissaoSub?: Subscription;
    private consultaDocumentoAtaSub?: Subscription;
    private consultaTextoInicioAtaSub?: Subscription;
    private consultaTextoFinalizacaoAtaSub?: Subscription;
    private consultaMembrosAtaSub?: Subscription;
    private consultaConvidadosAtaSub?: Subscription;
    private consultaAtividadesAtaSub?: Subscription;
    private consultaModelosAtaSub?: Subscription;
    private consultaAutosaveAtaSub?: Subscription;
    private gerarProtocoloAtaSub?: Subscription;
    private salvarAtaSub?: Subscription;
    private autosaveAtaSub?: Subscription;
    private inserirModeloAtaSub?: Subscription;
    private baixarDocumentoAtaSub?: Subscription;
    private atualizarAssistenteAtaSub?: Subscription;
    private atualizarParticipanteAtaSub?: Subscription;
    private atualizarGestoresAtaSub?: Subscription;
    private adicionarConvidadoAtaSub?: Subscription;
    private removerConvidadoAtaSub?: Subscription;
    private acaoReuniaoSub?: Subscription;
    private salvarReuniaoSub?: Subscription;
    private salvarAtividadeAtaSub?: Subscription;
    private excluirAtividadeAtaSub?: Subscription;
    private queryParamsSub?: Subscription;
    private acaoTabelaQueryPendente: AcaoTabelaComissoes | null = null;
    private idReuniaoDocumentoAtaConsulta: number | null = null;
    private idReuniaoTextoInicioAtaConsulta: number | null = null;
    private idReuniaoTextoFinalizacaoAtaConsulta: number | null = null;
    private idReuniaoMembrosAtaConsulta: number | null = null;
    private idReuniaoConvidadosAtaConsulta: number | null = null;
    private idReuniaoProtocoloAtaConsulta: number | null = null;
    private readonly acoesComSelecao: ReadonlySet<AcaoComissoes> = new Set(['cancelar', 'convocacoes', 'ata', 'visualizar']);
    private itensAcoesCabecalho?: AppMenuItem[];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly comissaoReuniaoService: ComissaoReuniaoService,
        private readonly comissaoAtividadeService: ComissaoAtividadeService,
        private readonly comissaoCadastroService: ComissaoCadastroService,
        private readonly confirmationService: ConfirmationService,
        private readonly messageService: MessageService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        const parentParams$ = this.route.parent?.paramMap;
        this.paramsSub = parentParams$?.subscribe((params) => {
            const tipoParam = params.get('tipo');
            this.tipo = tipoParam === 'atividades' ? 'atividades' : 'acompanhamento';
            this.filtrosAtivos = criarFiltrosComissoesIniciais();
            this.reuniaoSelecionada = null;
            this.ultimaAcaoExecutada = '';
            this.telaAtaVisivel = false;
            this.limparEstadoDocumentoAta();
            this.reiniciarPaginacaoReunioes();

            if (this.tipo === 'acompanhamento') {
                this.carregarStatusAta();
                this.carregarLocaisReuniao();
                this.aoCarregarEstruturasComissao();
                this.carregarAssistentes();
                this.carregarSituacoesAtividade();
                this.carregarReunioes(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaReunioes);
            } else {
                this.reiniciarEstadoAcompanhamento();
            }
            this.cdr.markForCheck();
        });

        this.queryParamsSub = this.route.queryParams.subscribe((queryParams) => {
            const reuniaoIdStr = queryParams['reuniao'];
            if (reuniaoIdStr && this.tipo === 'acompanhamento') {
                const idReuniao = Number(reuniaoIdStr);
                if (Number.isFinite(idReuniao) && idReuniao > 0) {
                    this.acaoTabelaQueryPendente = this.normalizarAcaoTabelaQuery(queryParams['acao']);
                    this.focarReuniao(idReuniao);
                }
            }
            this.cdr.markForCheck();
        });

        // A rota `item/:codigo` existia mas ninguém lia o parâmetro: abrir o link de uma
        // reunião caía na lista inteira. Reaproveita o mesmo filtro do query param `?reuniao=`.
        this.codigoRotaSub = this.route.paramMap.subscribe((params) => {
            const idReuniao = Number(params.get('codigo'));
            if (Number.isFinite(idReuniao) && idReuniao > 0 && this.tipo === 'acompanhamento') {
                this.focarReuniao(idReuniao);
            }
            this.cdr.markForCheck();
        });
    }

    /** Restringe a listagem a uma única reunião, pelo código. */
    private focarReuniao(idReuniao: number): void {
        this.filtrosAtivos.periodo = 'codigo-reuniao';
        this.filtrosAtivos.codigoReuniao = String(idReuniao);
        this.carregarReunioes(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaReunioes);
    }

    ngOnDestroy(): void {
        this.paramsSub?.unsubscribe();
        this.codigoRotaSub?.unsubscribe();
        this.consultaReunioesSub?.unsubscribe();
        this.consultaDashboardSummarySub?.unsubscribe();
        this.consultaStatusAtaSub?.unsubscribe();
        this.consultaLocaisSub?.unsubscribe();
        this.consultaAssistentesSub?.unsubscribe();
        this.consultaEstruturasComissaoSub?.unsubscribe();
        this.consultaDocumentoAtaSub?.unsubscribe();
        this.consultaTextoInicioAtaSub?.unsubscribe();
        this.consultaTextoFinalizacaoAtaSub?.unsubscribe();
        this.consultaMembrosAtaSub?.unsubscribe();
        this.consultaConvidadosAtaSub?.unsubscribe();
        this.consultaAtividadesAtaSub?.unsubscribe();
        this.consultaModelosAtaSub?.unsubscribe();
        this.consultaAutosaveAtaSub?.unsubscribe();
        this.gerarProtocoloAtaSub?.unsubscribe();
        this.salvarAtaSub?.unsubscribe();
        this.autosaveAtaSub?.unsubscribe();
        this.inserirModeloAtaSub?.unsubscribe();
        this.baixarDocumentoAtaSub?.unsubscribe();
        this.atualizarAssistenteAtaSub?.unsubscribe();
        this.atualizarParticipanteAtaSub?.unsubscribe();
        this.atualizarGestoresAtaSub?.unsubscribe();
        this.adicionarConvidadoAtaSub?.unsubscribe();
        this.removerConvidadoAtaSub?.unsubscribe();
        this.acaoReuniaoSub?.unsubscribe();
        this.salvarReuniaoSub?.unsubscribe();
        this.salvarAtividadeAtaSub?.unsubscribe();
        this.excluirAtividadeAtaSub?.unsubscribe();
        this.queryParamsSub?.unsubscribe();
    }

    get descricaoTipo(): string {
        return this.tipo === 'atividades' ? 'Atividades' : 'Acompanhamento';
    }

    alternarFiltros(): void {
        this.filtrosVisiveis.update((visivel) => !visivel);
    }

    /**
     * Ao estreitar, a coluna lateral não cabe e os filtros fecham. Alargar de volta não os
     * reabre: quem os abriu foi o usuário, e reabrir sozinho desfaria a escolha dele.
     */
    aoRedimensionarJanela(): void {
        const estreita = window.innerWidth <= 1200;
        if (estreita === this.telaEstreitaFiltros) {
            return;
        }
        this.telaEstreitaFiltros = estreita;
        if (estreita) {
            this.filtrosVisiveis.set(false);
        }
    }

    aoAplicarFiltros(filtros: FiltrosComissoes): void {
        if (this.tipo !== 'acompanhamento') {
            return;
        }

        this.reiniciarPaginacaoReunioes();
        this.carregarReunioes(filtros, this.paginaAtual(), this.linhasPorPaginaReunioes);
    }

    aoLimparFiltros(): void {
        this.filtrosAtivos = criarFiltrosComissoesIniciais();
        this.reuniaoSelecionada = null;
        this.reiniciarPaginacaoReunioes();

        if (this.tipo === 'acompanhamento') {
            this.carregarReunioes(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaReunioes);
        }
    }

    aoSelecionarReuniao(reuniao: ReuniaoComissao | null): void {
        this.reuniaoSelecionada = reuniao;
    }

    aoCarregarEstruturasComissao(): void {
        if (this.carregandoEstruturasComissao) {
            return;
        }

        this.carregandoEstruturasComissao = true;
        this.erroEstruturasComissao = '';

        this.consultaEstruturasComissaoSub?.unsubscribe();
        this.consultaEstruturasComissaoSub = forkJoin({
            tipos: this.comissaoReuniaoService.listarTiposComissao(),
            estruturas: this.comissaoReuniaoService.listarFiltrosComissao(ID_ESTRUTURA_TODAS_COMISSOES)
        }).subscribe({
            next: ({ tipos, estruturas }) => {
                this.tiposComissao = tipos ?? [];
                this.estruturasComissao = estruturas ?? [];
                this.carregandoEstruturasComissao = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.tiposComissao = [];
                this.estruturasComissao = [];
                this.erroEstruturasComissao = 'Não foi possível carregar filtros de comissão.';
                this.carregandoEstruturasComissao = false;
                this.cdr.markForCheck();
            }
        });
    }

    aoMudarPaginaReunioes(event: TablePageEvent): void {
        if (this.tipo !== 'acompanhamento') {
            return;
        }

        const primeiraLinha = presenter.normalizarInteiroNaoNegativo(event.first);
        const linhas = presenter.normalizarLinhasPorPagina(event.rows);

        this.primeiraLinhaReunioes = primeiraLinha;
        this.linhasPorPaginaReunioes = linhas;
        this.carregarReunioes(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaReunioes);
    }

    itensAcoes(): AppMenuItem[] {
        if (!this.itensAcoesCabecalho) {
            this.itensAcoesCabecalho = [this.criarItemAcao('cadastrar', 'Cadastrar', 'pi pi-plus', 'bg-green-500', PERMISSOES.comissoes.reunioes.criar), this.criarItemAcao('relatorio', 'Relatório', 'pi pi-file', 'bg-teal-500')];
        }

        for (const item of this.itensAcoesCabecalho) {
            const acao = item.id as AcaoComissoes | undefined;
            item.disabled = Boolean(acao && this.acoesComSelecao.has(acao) && !this.reuniaoSelecionada);
        }

        return this.itensAcoesCabecalho;
    }

    aoExecutarAcao(acao: AcaoComissoes): void {
        if (acao === 'cadastrar') {
            this.abrirFormularioReuniao(null);
            return;
        }

        if (acao === 'cancelar' && this.reuniaoSelecionada) {
            this.cancelarReuniaoSelecionada();
            return;
        }

        if (acao === 'convocacoes' && this.reuniaoSelecionada) {
            this.abrirGerenciarConvocacoes();
            return;
        }

        const rotulosAcao: Record<AcaoComissoes, string> = {
            cadastrar: 'Cadastro iniciado',
            cancelar: 'Cancelamento solicitado',
            convocacoes: 'Convocações abertas',
            ata: 'Ata aberta',
            relatorio: 'Relatório em preparação',
            visualizar: 'Visualização detalhada'
        };

        const reuniao = this.reuniaoSelecionada ? ` na reunião ${this.reuniaoSelecionada.codigo}` : '';
        this.ultimaAcaoExecutada = `${rotulosAcao[acao]}${reuniao}.`;
    }

    aoExecutarAcaoTabela(acao: AcaoTabelaComissoes): void {
        switch (acao) {
            case 'abrir-ata':
                if (this.reuniaoSelecionada && this.reuniaoPermiteAtaEStatus(this.reuniaoSelecionada)) {
                    this.abrirAtaReuniao(this.reuniaoSelecionada);
                } else {
                    this.ultimaAcaoExecutada = 'A ata não pode ser aberta para o status desta reunião.';
                }
                break;
            case 'editar':
                if (this.reuniaoSelecionada) {
                    this.abrirFormularioReuniao(this.reuniaoSelecionada);
                }
                break;
            case 'cancelar':
                this.cancelarReuniaoSelecionada();
                break;
            case 'excluir':
                this.excluirReuniaoSelecionada();
                break;
            case 'convocacoes':
                this.abrirGerenciarConvocacoes();
                break;
            case 'ir-para-atividades':
                if (this.reuniaoSelecionada) {
                    this.router.navigate(['/comissoes/atividades'], {
                        queryParams: { reuniao: this.reuniaoSelecionada.codigo }
                    });
                }
                break;
            case 'visualizar-comissao-nucleo':
                if (this.reuniaoSelecionada?.idEstruturaOrganizacional) {
                    this.router.navigate(['/comissoes/acompanhamento/comissoes'], {
                        queryParams: { id: this.reuniaoSelecionada.idEstruturaOrganizacional }
                    });
                }
                break;
            case 'nova-atividade-reuniao':
                if (this.reuniaoSelecionada && this.reuniaoPermiteNovaAtividade(this.reuniaoSelecionada)) {
                    this.router.navigate(['/comissoes/atividades'], {
                        queryParams: { reuniao: this.reuniaoSelecionada.codigo, novaAtividade: 'true' }
                    });
                } else {
                    this.ultimaAcaoExecutada = 'Não e permitido inserir nova atividade para o status desta reunião.';
                }
                break;
            case 'reativar-reuniao':
                if (this.reuniaoSelecionada && this.idStatusReuniao(this.reuniaoSelecionada) === 5) {
                    this.reativarReuniaoSelecionada();
                } else {
                    this.ultimaAcaoExecutada = 'Apenas reuniões excluidas podem ser reativadas.';
                }
                break;
            case 'status-ata-anterior':
                this.atualizarStatusAtaSelecionada('anterior');
                break;
            case 'status-ata-proximo':
                this.atualizarStatusAtaSelecionada('proximo');
                break;
            case 'visualizar-selecionada':
                if (this.reuniaoSelecionada) {
                    this.ultimaAcaoExecutada = `Visualização da reunião ${this.reuniaoSelecionada.codigo}.`;
                }
                break;
        }
    }
    aoVoltarDaAta(): void {
        this.telaAtaVisivel = false;
        this.limparEstadoDocumentoAta();
        this.carregarReunioes(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaReunioes);
    }

    aoSolicitarInicioAta(): void {
        if (!this.reuniaoSelecionada) {
            return;
        }

        this.carregarTextoInicioAta(this.reuniaoSelecionada);
    }

    aoSolicitarFinalizacaoAta(): void {
        if (!this.reuniaoSelecionada) {
            return;
        }

        this.carregarTextoFinalizacaoAta(this.reuniaoSelecionada);
    }

    aoSalvarAta(payload: { conteudoHtml: string; inImportante: string; dsImportante?: string }): void {
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoSelecionada?.codigo);
        if (idReuniao === null) {
            return;
        }

        this.salvandoManual = true;
        this.autosaveAtaSub?.unsubscribe();

        this.salvarAtaSub?.unsubscribe();
        this.salvarAtaSub = this.comissaoReuniaoService
            .salvarAta(idReuniao, {
                txDocumentoHtml: payload.conteudoHtml,
                inImportante: payload.inImportante,
                dsImportante: payload.dsImportante,
                etag: this.etagAtaAtual
            })
            .subscribe({
                next: (response) => {
                    this.salvandoManual = false;
                    this.etagAtaAtual = response?.etag ?? this.etagAtaAtual;
                    this.ultimaAcaoExecutada = `Ata da reunião ${idReuniao} salva.`;
                    this.aplicarImportanteEstadoReuniao(idReuniao, payload.inImportante, payload.dsImportante);
                    this.cdr.detectChanges();
                },
                error: (erro) => {
                    this.salvandoManual = false;
                    if (erro?.status === 409) {
                        this.tratarConflitoAta();
                    } else {
                        this.ultimaAcaoExecutada = 'Não foi possível salvar a ata.';
                    }
                    this.cdr.detectChanges();
                }
            });
    }

    private carregarSituacoesAtividade(): void {
        this.comissaoAtividadeService.listarSituacoesAtividade().subscribe({
            next: (situacoes) => {
                this.situacoesAtividade = situacoes ?? [];
                this.cdr.markForCheck();
            },
            error: () => {
                this.situacoesAtividade = [];
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar as situações da atividade.' });
                this.cdr.markForCheck();
            }
        });
    }

    aoAutosaveAta(payload: { conteudoHtml: string; inImportante: string; dsImportante?: string }): void {
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoSelecionada?.codigo);
        if (idReuniao === null || this.salvandoManual || this.autosavePausadoAta) {
            return;
        }

        this.autosaveAtaSub?.unsubscribe();
        this.autosaveAtaSub = this.comissaoReuniaoService
            .salvarAta(idReuniao, {
                txDocumentoHtml: payload.conteudoHtml,
                inImportante: payload.inImportante,
                dsImportante: payload.dsImportante,
                etag: this.etagAtaAtual
            })
            .subscribe({
                next: (response) => {
                    if (!this.salvandoManual) {
                        this.etagAtaAtual = response?.etag ?? this.etagAtaAtual;
                        this.aplicarImportanteEstadoReuniao(idReuniao, payload.inImportante, payload.dsImportante);
                        this.cdr.detectChanges();
                    }
                },
                error: (erro) => {
                    if (erro?.status === 409) {
                        this.tratarConflitoAta();
                        this.cdr.detectChanges();
                    } else {
                        this.messageService.add({ severity: 'warn', summary: 'Autosave', detail: 'Não foi possível salvar automaticamente a ata.' });
                    }
                }
            });
    }

    private tratarConflitoAta(): void {
        this.autosavePausadoAta = true;
        this.conflitoAtaMensagem = 'A ata foi alterada por outro usuário. Recarregue para continuar editando.';
        this.messageService.add({ severity: 'warn', summary: 'Conflito', detail: this.conflitoAtaMensagem });
    }

    aoRecarregarAta(): void {
        const reuniao = this.reuniaoSelecionada;
        if (!reuniao) {
            return;
        }
        this.conflitoAtaMensagem = '';
        this.autosavePausadoAta = false;
        this.carregarDocumentoAtaReuniao(reuniao);
    }

    private aplicarImportanteEstadoReuniao(idReuniao: number, inImportante: string, dsImportante?: string): void {
        const codigo = String(idReuniao);
        const aplicar = (item: ReuniaoComissao): ReuniaoComissao => {
            if (item.codigo !== codigo) {
                return item;
            }

            return {
                ...item,
                ataMarcada: inImportante === 'S',
                importante: inImportante,
                dsImportante: dsImportante
            };
        };

        this.reunioesPorTipo = this.reunioesPorTipo.map(aplicar);
        this.reunioesFiltradas = this.reunioesFiltradas.map(aplicar);

        if (this.reuniaoSelecionada?.codigo === codigo) {
            this.reuniaoSelecionada = aplicar(this.reuniaoSelecionada);
        }
    }

    aoInserirModeloAta(idModeloAta: number): void {
        void idModeloAta;
        // Chamada desativada temporariamente: endpoint usa a tabela SCCT_MODELO_ATA.
        /*
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoSelecionada?.codigo);
        if (idReuniao === null || !Number.isFinite(idModeloAta) || idModeloAta <= 0) {
            return;
        }

        this.inserirModeloAtaSub?.unsubscribe();
        this.inserirModeloAtaSub = this.comissaoReuniaoService
            .inserirModeloAta(idReuniao, {
                idModeloAta,
                posicao: 'FIM'
            })
            .subscribe({
                next: () => {
                    this.ultimaAcaoExecutada = 'Modelo inserido na ata.';
                    if (this.reuniaoSelecionada) {
                        this.carregarDocumentoAtaReuniao(this.reuniaoSelecionada);
                    }
                },
                error: () => {
                    this.ultimaAcaoExecutada = 'Não foi possível inserir o modelo na ata.';
                }
            });
        */
    }

    aoBaixarDocumentoAta(): void {
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoSelecionada?.codigo);
        if (idReuniao === null) {
            return;
        }

        this.baixarDocumentoAtaSub?.unsubscribe();
        this.baixarDocumentoAtaSub = this.comissaoReuniaoService.baixarDocumentoAta(idReuniao).subscribe({
            next: (blob) => this.baixarBlobAta(blob, idReuniao),
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível baixar o documento da ata.';
                this.cdr.markForCheck();
            }
        });
    }

    aoAlterarAssistenteAta(valor: string): void {
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoSelecionada?.codigo);
        if (idReuniao === null) {
            return;
        }

        const assistente = this.parseIdUfDoFiltro(valor);
        this.atualizarAssistenteAtaSub?.unsubscribe();
        this.atualizarAssistenteAtaSub = this.comissaoReuniaoService
            .atualizarAssistenteReuniao(idReuniao, {
                idSolicitanteAssistente: assistente?.id ?? null,
                ufSolicitanteAssistente: assistente?.uf ?? null
            })
            .subscribe({
                next: () => {
                    this.ultimaAcaoExecutada = `Assistente da reunião ${idReuniao} atualizado.`;
                    if (this.reuniaoSelecionada) {
                        const opcao = this.opcoesAssistentes.find((item) => item.valor === valor);
                        this.reuniaoSelecionada = {
                            ...this.reuniaoSelecionada,
                            assistente: opcao?.rotulo ?? '-',
                            idSolicitanteAssistente: assistente?.id,
                            ufSolicitanteAssistente: assistente?.uf
                        };
                    }
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.ultimaAcaoExecutada = 'Não foi possível atualizar o assistente.';
                    this.cdr.markForCheck();
                }
            });
    }

    aoEditarReuniaoDaAta(): void {
        if (!this.reuniaoSelecionada) {
            return;
        }
        this.abrirFormularioReuniao(this.reuniaoSelecionada);
    }

    aoAlterarGestoresAta(payload: { coordenador?: ParticipanteAta | null; secretario?: ParticipanteAta | null; assistenteValor?: string }): void {
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoSelecionada?.codigo);
        const reuniaoAtual = this.reuniaoSelecionada;
        if (idReuniao === null || !reuniaoAtual) {
            return;
        }

        if (payload.coordenador && payload.secretario && payload.coordenador.idSolicitante === payload.secretario.idSolicitante && payload.coordenador.uf === payload.secretario.uf) {
            this.messageService.add({ severity: 'warn', summary: 'Gestores', detail: 'Coordenador e secretário devem ser pessoas diferentes.' });
            return;
        }

        const operacoes: Observable<unknown>[] = [];
        if (payload.coordenador) {
            operacoes.push(
                this.garantirCoordenadorRegistroReuniao(idReuniao, reuniaoAtual, { idSolicitante: payload.coordenador.idSolicitante, uf: payload.coordenador.uf }).pipe(
                    switchMap(() =>
                        this.comissaoReuniaoService.atualizarParticipanteAta(idReuniao, {
                            idSolicitante: payload.coordenador!.idSolicitante,
                            ufSgUf: payload.coordenador!.uf,
                            inPresenca: this.obterPresencaGestor(payload.coordenador!),
                            inFuncao: 'C'
                        })
                    )
                )
            );
        }
        if (payload.secretario) {
            operacoes.push(
                this.comissaoReuniaoService.atualizarParticipanteAta(idReuniao, {
                    idSolicitante: payload.secretario.idSolicitante,
                    ufSgUf: payload.secretario.uf,
                    inPresenca: this.obterPresencaGestor(payload.secretario),
                    inFuncao: 'S'
                })
            );
        }
        if (payload.assistenteValor !== undefined) {
            const assistente = this.parseIdUfDoFiltro(payload.assistenteValor);
            operacoes.push(
                this.comissaoReuniaoService.atualizarAssistenteReuniao(idReuniao, {
                    idSolicitanteAssistente: assistente?.id ?? null,
                    ufSolicitanteAssistente: assistente?.uf ?? null
                })
            );
        }

        if (!operacoes.length) {
            return;
        }

        this.atualizarGestoresAtaSub?.unsubscribe();
        this.atualizarGestoresAtaSub = forkJoin(operacoes).subscribe({
            next: () => {
                this.ultimaAcaoExecutada = `Gestores da reunião ${idReuniao} atualizados.`;
                if (this.reuniaoSelecionada) {
                    const assistente = this.parseIdUfDoFiltro(payload.assistenteValor ?? '');
                    const opcao = this.opcoesAssistentes.find((item) => item.valor === payload.assistenteValor);
                    this.reuniaoSelecionada = {
                        ...this.reuniaoSelecionada,
                        assistente: payload.assistenteValor !== undefined ? (opcao?.rotulo ?? '-') : this.reuniaoSelecionada.assistente,
                        idSolicitanteAssistente: payload.assistenteValor !== undefined ? (assistente?.id ?? undefined) : this.reuniaoSelecionada.idSolicitanteAssistente,
                        ufSolicitanteAssistente: payload.assistenteValor !== undefined ? (assistente?.uf ?? undefined) : this.reuniaoSelecionada.ufSolicitanteAssistente,
                        idSolicitanteCoordenador: payload.coordenador?.idSolicitante ?? this.reuniaoSelecionada.idSolicitanteCoordenador,
                        ufSolicitanteCoordenador: payload.coordenador?.uf ?? this.reuniaoSelecionada.ufSolicitanteCoordenador,
                        nomeCoordenador: payload.coordenador?.nome ?? this.reuniaoSelecionada.nomeCoordenador
                    };
                    this.carregarMembrosAtaReuniao(this.reuniaoSelecionada);
                    this.carregarConvidadosAtaReuniao(this.reuniaoSelecionada);
                }
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível atualizar os gestores da ata.';
                this.cdr.markForCheck();
            }
        });
    }

    aoAlterarParticipanteAta(payload: { participante: ParticipanteAta; tipo: 'M' | 'C'; inPresenca?: string; inFuncao?: string }): void {
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoSelecionada?.codigo);
        if (idReuniao === null) {
            return;
        }

        const request: ComissaoAtaParticipanteRequest = {
            idSolicitante: payload.participante.idSolicitante,
            ufSgUf: payload.participante.uf,
            inPresenca: payload.inPresenca,
            inFuncao: payload.inFuncao
        };

        this.atualizarParticipanteAtaSub?.unsubscribe();
        this.atualizarParticipanteAtaSub = this.comissaoReuniaoService.atualizarParticipanteAta(idReuniao, request).subscribe({
            next: () => {
                this.carregarMembrosAtaReuniao(this.reuniaoSelecionada!);
                this.carregarConvidadosAtaReuniao(this.reuniaoSelecionada!);
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível atualizar a presença.';
                this.cdr.markForCheck();
            }
        });
    }

    aoAdicionarConvidadoAta(payload: { idSolicitante: number; ufSgUf: string }): void {
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoSelecionada?.codigo);
        if (idReuniao === null) {
            return;
        }

        this.adicionarConvidadoAtaSub?.unsubscribe();
        this.adicionarConvidadoAtaSub = this.comissaoReuniaoService.adicionarConvidadoAta(idReuniao, payload).subscribe({
            next: () => this.carregarConvidadosAtaReuniao(this.reuniaoSelecionada!),
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível adicionar o convidado.';
                this.cdr.markForCheck();
            }
        });
    }

    aoRemoverConvidadoAta(convidado: ParticipanteAta): void {
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoSelecionada?.codigo);
        if (idReuniao === null) {
            return;
        }

        const idSolicitante = presenter.numeroSeguro(convidado.idSolicitante);
        const ufSgUf = presenter.normalizarUf(convidado.uf);
        if (idSolicitante <= 0 || !ufSgUf) {
            this.ultimaAcaoExecutada = 'Não foi possível identificar o convidado para remoção.';
            return;
        }

        this.removerConvidadoAtaSub?.unsubscribe();
        this.removerConvidadoAtaSub = this.comissaoReuniaoService.removerConvidadoAta(idReuniao, idSolicitante, ufSgUf).subscribe({
            next: () => {
                this.convidadosAta = this.convidadosAta.filter((item) => item.idSolicitante !== idSolicitante || presenter.normalizarUf(item.uf) !== ufSgUf);
                this.ultimaAcaoExecutada = 'Convidado removido da ata.';
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível remover o convidado.';
                this.cdr.markForCheck();
            }
        });
    }

    aoSalvarAtividadeAta(payload: { idTarefa?: number; request: ComissaoAtividadeSalvarRequest }): void {
        const obs: Observable<unknown> = payload.idTarefa ? this.comissaoAtividadeService.atualizarAtividade(payload.idTarefa, payload.request) : this.comissaoAtividadeService.cadastrarAtividade(payload.request);

        this.salvarAtividadeAtaSub?.unsubscribe();
        this.salvarAtividadeAtaSub = obs.subscribe({
            next: () => {
                this.ultimaAcaoExecutada = payload.idTarefa ? 'Atividade atualizada com sucesso.' : 'Atividade cadastrada com sucesso.';
                if (this.reuniaoSelecionada) {
                    this.carregarAtividadesAtaReuniao(this.reuniaoSelecionada);
                }
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível salvar a atividade.';
                this.cdr.markForCheck();
            }
        });
    }

    aoExcluirAtividadeAta(idTarefa: number): void {
        this.excluirAtividadeAtaSub?.unsubscribe();
        this.excluirAtividadeAtaSub = this.comissaoAtividadeService.excluirAtividade(idTarefa).subscribe({
            next: () => {
                this.ultimaAcaoExecutada = 'Atividade excluída com sucesso.';
                if (this.reuniaoSelecionada) {
                    this.carregarAtividadesAtaReuniao(this.reuniaoSelecionada);
                }
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível excluir a atividade.';
                this.cdr.markForCheck();
            }
        });
    }

    aoAlterarAtividadeResolvidaAta(payload: { idTarefa: number; resolvida: boolean }): void {
        if (!this.reuniaoSelecionada) return;

        const item = this.atividadesAta.find((a) => a.idTarefa === payload.idTarefa);
        if (!item) return;

        const request: ComissaoAtividadeSalvarRequest = {
            idEstruturaOrganizacional: this.reuniaoSelecionada.idEstruturaOrganizacional,
            idSolicitanteResponsavel: item.idSolicitanteResponsavel,
            sgUfSolicitanteResponsavel: item.sgUfSolicitanteResponsavel,
            dsTarefa: item.descricao,
            txObservacao: item.txObservacao,
            dtInicio: item.dtInicio,
            dtTermino: item.dtTermino,
            idSituacaoTarefa: payload.resolvida ? 2 : 1,
            idReuniao: Number(this.reuniaoSelecionada.codigo)
        };

        this.salvarAtividadeAtaSub?.unsubscribe();
        this.salvarAtividadeAtaSub = this.comissaoAtividadeService.atualizarAtividade(payload.idTarefa, request).subscribe({
            next: () => {
                this.ultimaAcaoExecutada = 'Status da atividade atualizado.';
                if (this.reuniaoSelecionada) {
                    this.carregarAtividadesAtaReuniao(this.reuniaoSelecionada);
                }
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível atualizar o status da atividade.';
                this.cdr.markForCheck();
            }
        });
    }

    aoSalvarFormularioReuniao(payload: ReuniaoSalvarPayload): void {
        const idReuniao = presenter.parseNumeroPositivo(this.reuniaoFormulario?.codigo);
        const salvarVeioDaAta = this.telaAtaVisivel && idReuniao !== null && this.reuniaoSelecionada?.codigo === this.reuniaoFormulario?.codigo;
        const operacao: Observable<ComissaoReuniaoSalvarResponse> = idReuniao === null ? this.comissaoReuniaoService.cadastrarReuniao(payload) : this.comissaoReuniaoService.atualizarReuniao(idReuniao, payload);

        this.salvarReuniaoSub?.unsubscribe();
        this.salvarReuniaoSub = operacao.subscribe({
            next: (response) => {
                const codigo = response.idReuniao ?? idReuniao;
                this.ultimaAcaoExecutada = codigo ? `Reunião ${codigo} salva.` : 'Reunião salva.';
                this.formularioReuniaoVisivel = false;
                this.reuniaoFormulario = null;

                if (salvarVeioDaAta && this.reuniaoSelecionada) {
                    this.reuniaoSelecionada = this.aplicarPayloadFormularioNaReuniao(this.reuniaoSelecionada, payload);
                    this.carregarDadosAtaReuniao(this.reuniaoSelecionada);
                    this.cdr.markForCheck();
                    return;
                }

                this.carregarReunioes(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaReunioes);
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível salvar a reunião.';
                this.cdr.markForCheck();
            }
        });
    }

    aoAtualizarConvocacoes(): void {
        this.carregarReunioes(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaReunioes);
    }

    private abrirFormularioReuniao(reuniao: ReuniaoComissao | null): void {
        if (reuniao && (reuniao.idStatusReuniao === 5 || reuniao.idStatusAta === 4 || reuniao.idStatusAta === 5)) {
            this.ultimaAcaoExecutada = 'Esta reunião não permite edição.';
            return;
        }
        this.reuniaoFormulario = reuniao;
        this.formularioReuniaoVisivel = true;
        if (!this.estruturasComissao.length) {
            this.aoCarregarEstruturasComissao();
        }
    }

    private aplicarPayloadFormularioNaReuniao(reuniao: ReuniaoComissao, payload: ReuniaoSalvarPayload): ReuniaoComissao {
        const local = this.locaisReuniao.find((item) => Number(item.idLocalReuniao ?? 0) === payload.idLocalReuniao);
        const estrutura = this.estruturasComissao.find((item) => Number(item.idEstruturaOrganizacional ?? 0) === payload.idEstruturaOrganizacional);
        const assistente = this.opcoesAssistentes.find((item) => item.valor === this.criarValorAssistente(payload.idSolicitanteAssistente, payload.ufSolicitanteAssistente));

        return {
            ...reuniao,
            visivelPortal: presenter.normalizarTexto(payload.inVisivelPortal) === 'S',
            modalidade: this.mapearModalidadeFormulario(payload.inModalidade),
            codigoComissao: String(payload.idEstruturaOrganizacional),
            idEstruturaOrganizacional: payload.idEstruturaOrganizacional,
            nomeComissao: estrutura?.dsEstruturaOrganizacional?.trim() || reuniao.nomeComissao,
            local: local?.dsLocalReuniao?.trim() || reuniao.local,
            idLocalReuniao: payload.idLocalReuniao,
            dataInicio: this.converterData(payload.dhInicioReuniao),
            dataTermino: this.converterData(payload.dhFimReuniao),
            assistente: assistente?.rotulo ?? reuniao.assistente,
            idSolicitanteAssistente: payload.idSolicitanteAssistente ?? undefined,
            ufSolicitanteAssistente: payload.ufSolicitanteAssistente ?? undefined,
            idSolicitanteCoordenador: payload.idSolicitanteCoordenador ?? reuniao.idSolicitanteCoordenador,
            ufSolicitanteCoordenador: payload.sgUfCoordenador ?? reuniao.ufSolicitanteCoordenador,
            tipoComissao: this.mapearTipoComissao(payload.idTipoComissaoCt, estrutura?.dsEstruturaOrganizacional),
            idTipoComissaoCt: payload.idTipoComissaoCt
        };
    }

    private criarValorAssistente(idSolicitante?: number | null, ufSgUf?: string | null): string {
        const id = Number(idSolicitante ?? 0);
        const uf = presenter.normalizarTexto(ufSgUf);
        return id > 0 && uf.length === 2 ? `id:${id}|uf:${uf}` : 'todos';
    }

    private mapearModalidadeFormulario(inModalidade: string): string {
        const modalidade = presenter.normalizarTexto(inModalidade);
        if (modalidade === 'P') {
            return 'Presencial';
        }
        if (modalidade === 'R') {
            return 'Virtual';
        }
        return 'Hibrida';
    }

    private abrirGerenciarConvocacoes(): void {
        if (!this.reuniaoSelecionada) {
            return;
        }
        if (this.reuniaoSelecionada.idStatusReuniao === 5) {
            this.ultimaAcaoExecutada = 'A reunião está excluída.';
            return;
        }
        this.gerenciarConvocacoesVisivel = true;
    }

    private cancelarReuniaoSelecionada(): void {
        const reuniao = this.reuniaoSelecionada;
        const idReuniao = presenter.parseNumeroPositivo(reuniao?.codigo);
        if (!reuniao || idReuniao === null) {
            return;
        }
        this.confirmationService.confirm({
            message: `Cancelar a reunião ${reuniao.codigo}?`,
            header: 'Confirmar cancelamento',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.executarAcaoReuniao(this.comissaoReuniaoService.cancelarReuniao(idReuniao), `Reunião ${reuniao.codigo} cancelada.`, 'Não foi possível cancelar a reunião.');
            }
        });
    }

    private excluirReuniaoSelecionada(): void {
        const reuniao = this.reuniaoSelecionada;
        const idReuniao = presenter.parseNumeroPositivo(reuniao?.codigo);
        if (!reuniao || idReuniao === null) {
            return;
        }
        this.confirmationService.confirm({
            message: `Excluir a reunião ${reuniao.codigo}?`,
            header: 'Confirmar exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.executarAcaoReuniao(this.comissaoReuniaoService.excluirReuniao(idReuniao), `Reunião ${reuniao.codigo} excluída.`, 'Não foi possível excluir a reunião.');
            }
        });
    }

    private reativarReuniaoSelecionada(): void {
        const reuniao = this.reuniaoSelecionada;
        const idReuniao = presenter.parseNumeroPositivo(reuniao?.codigo);
        if (!reuniao || idReuniao === null) {
            return;
        }
        this.executarAcaoReuniao(this.comissaoReuniaoService.reativarReuniao(idReuniao), `Reunião ${reuniao.codigo} reativada.`, 'Não foi possível reativar a reunião.');
    }

    private atualizarStatusAtaSelecionada(direcao: 'anterior' | 'proximo'): void {
        const reuniao = this.reuniaoSelecionada;
        const idReuniao = presenter.parseNumeroPositivo(reuniao?.codigo);
        if (!reuniao || idReuniao === null) {
            return;
        }

        if (!this.reuniaoPermiteAtaEStatus(reuniao)) {
            this.ultimaAcaoExecutada = 'Não e permitido modificar o status da ata para o status desta reunião.';
            return;
        }

        const idStatusAta = direcao === 'anterior' ? reuniao.idStatusAnterior : reuniao.idProximoStatus;
        if (!idStatusAta || idStatusAta <= 0) {
            this.ultimaAcaoExecutada = direcao === 'anterior' ? 'A ata não possui status anterior.' : 'A ata não possui próximo status.';
            return;
        }

        const descricao = direcao === 'anterior' ? reuniao.descricaoStatusAtaAnterior : reuniao.descricaoStatusAtaProximo;
        this.executarAcaoReuniao(
            this.comissaoReuniaoService.atualizarStatusAta(idReuniao, { idStatusAta }),
            `Status da ata da reunião ${reuniao.codigo} atualizado${descricao ? ` para ${descricao}` : ''}.`,
            'Não foi possível atualizar o status da ata.'
        );
    }

    private idStatusReuniao(reuniao: ReuniaoComissao): number | null {
        if (typeof reuniao.idStatusReuniao === 'number' && Number.isFinite(reuniao.idStatusReuniao)) {
            return reuniao.idStatusReuniao;
        }

        const mapa: Record<ReuniaoComissao['status'], number> = {
            agendada: 1,
            remarcada: 2,
            cancelada: 3,
            realizada: 4,
            excluida: 5
        };
        return mapa[reuniao.status] ?? null;
    }

    private reuniaoPermiteAtaEStatus(reuniao: ReuniaoComissao): boolean {
        const idStatusReuniao = this.idStatusReuniao(reuniao);
        return idStatusReuniao === 1 || idStatusReuniao === 2 || idStatusReuniao === 4;
    }

    private reuniaoPermiteNovaAtividade(reuniao: ReuniaoComissao): boolean {
        const idStatusReuniao = this.idStatusReuniao(reuniao);
        return idStatusReuniao === 1 || idStatusReuniao === 2;
    }

    private executarAcaoReuniao(operacao: Observable<unknown>, mensagemSucesso: string, mensagemErro: string): void {
        this.acaoReuniaoSub?.unsubscribe();
        this.acaoReuniaoSub = operacao.subscribe({
            next: () => {
                this.ultimaAcaoExecutada = mensagemSucesso;
                this.carregarReunioes(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaReunioes);
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = mensagemErro;
                this.cdr.markForCheck();
            }
        });
    }

    private criarItemAcao(acao: AcaoComissoes, label: string, icon: string, styleClass: string, permission?: AppMenuItem['permission']): AppMenuItem {
        const precisaSelecao = this.acoesComSelecao.has(acao);
        const desabilitado = precisaSelecao && !this.reuniaoSelecionada;

        return {
            id: acao,
            label,
            icon,
            styleClass,
            disabled: desabilitado,
            permission,
            command: () => this.aoExecutarAcao(acao)
        };
    }

    private carregarReunioes(filtros: FiltrosComissoes, pagina: number, tamanhoPagina: number): void {
        this.filtrosAtivos = { ...filtros };
        this.reuniaoSelecionada = null;
        this.erroConsulta = '';
        this.carregandoReunioes = true;

        const paginaNormalizada = presenter.normalizarInteiroNaoNegativo(pagina);
        const tamanhoPaginaNormalizado = presenter.normalizarLinhasPorPagina(tamanhoPagina);
        this.primeiraLinhaReunioes = paginaNormalizada * tamanhoPaginaNormalizado;
        this.linhasPorPaginaReunioes = tamanhoPaginaNormalizado;

        const requestFiltros = this.mapearFiltrosParaRequest(filtros);

        this.consultaReunioesSub?.unsubscribe();
        this.consultaReunioesSub = this.comissaoReuniaoService.consultarReunioes(requestFiltros, paginaNormalizada, tamanhoPaginaNormalizado).subscribe({
            next: (pagina) => {
                const reunioesMapeadas = (pagina.content ?? []).map((item) => this.mapearReuniao(item));
                this.reunioesPorTipo = reunioesMapeadas;
                this.reunioesFiltradas = this.aplicarFiltrosComplementares(reunioesMapeadas, filtros);
                this.totalRegistrosReunioes = presenter.numeroSeguro(pagina.totalElements ?? reunioesMapeadas.length);
                this.carregandoReunioes = false;
                this.executarAcaoTabelaQueryPendente();
                this.cdr.markForCheck();
            },
            error: () => {
                this.reiniciarEstadoAcompanhamento();
                this.erroConsulta = 'Não foi possível carregar as reuniões de comissões.';
                this.carregandoReunioes = false;
                this.cdr.markForCheck();
            }
        });

        this.consultaDashboardSummarySub?.unsubscribe();
        this.consultaDashboardSummarySub = this.comissaoReuniaoService.consultarDashboardSummary(requestFiltros).subscribe({
            next: (summary) => {
                this.dashboardSummary = summary;
                this.cdr.markForCheck();
            },
            error: () => {
                this.dashboardSummary = null;
                this.cdr.markForCheck();
            }
        });
    }

    private mapearFiltrosParaRequest(filtros: FiltrosComissoes): ComissaoReuniaoFiltroRequest {
        const codigoReuniao = presenter.parseNumeroPositivo(filtros.codigoReuniao);
        const idStatusReuniao = this.mapearIdStatusReuniao(filtros.status);
        const request: ComissaoReuniaoFiltroRequest = {
            tipoPeriodo: codigoReuniao !== null ? EComissaoReuniaoTipoPeriodoFiltro.CODIGO_REUNIAO : this.mapearTipoPeriodo(filtros.periodo)
        };

        if (idStatusReuniao === null) {
            request.excluirStatusReuniaoExcluida = true;
        } else {
            request.idStatusReuniao = idStatusReuniao;
            request.excluirStatusReuniaoExcluida = false;
        }

        if (codigoReuniao !== null) {
            request.codigoReuniao = codigoReuniao;
        }

        if (filtros.periodo === 'por-periodo' && codigoReuniao === null) {
            if (filtros.dataInicial) {
                request.dataInicioReuniao = filtros.dataInicial;
            }
            if (filtros.dataFinal) {
                request.dataFimReuniao = filtros.dataFinal;
            }
        }

        if (filtros.local !== 'todos') {
            const idLocal = this.parseIdDoFiltro(filtros.local);
            if (idLocal !== null) {
                request.idLocalReuniao = idLocal;
            }
        }

        if (filtros.convocacoes === 'pendentes') {
            request.tipoConvocacao = EComissaoReuniaoTipoConvocacaoFiltro.CONVOCACOES_PENDENTES;
        } else if (filtros.convocacoes === 'confirmacoes-pendentes') {
            request.tipoConvocacao = EComissaoReuniaoTipoConvocacaoFiltro.CONFIRMACOES_PENDENTES;
        }

        if (filtros.atas === 'nao-marcadas') {
            request.marcacaoAta = EComissaoReuniaoMarcacaoAtaFiltro.NAO_IMPORTANTE;
        } else if (filtros.atas === 'importantes') {
            request.marcacaoAta = EComissaoReuniaoMarcacaoAtaFiltro.IMPORTANTE;
        } else {
            request.marcacaoAta = EComissaoReuniaoMarcacaoAtaFiltro.TODAS;
        }

        const idStatusAta = this.parseIdStatusAtaFiltro(filtros.statusAta);
        if (idStatusAta !== null) {
            request.idStatusAta = idStatusAta;
        }

        const assistente = this.parseIdUfDoFiltro(filtros.assistente);
        if (assistente) {
            request.idSolicitanteAssistente = assistente.id;
            request.ufSolicitanteAssistente = assistente.uf;
        }

        const idTipoComissaoCt = this.mapearIdTipoComissaoCt(filtros.tipoComissao);
        if (idTipoComissaoCt !== null) {
            request.idTipoComissaoCt = idTipoComissaoCt;
        }

        const membroConvidado = this.parseIdUfDoTexto(filtros.membroConvidado);
        if (membroConvidado) {
            request.idMembroConvidado = membroConvidado.id;
            request.ufMembroConvidado = membroConvidado.uf;
        }

        const idEstrutura = this.parseIdEstruturaFiltro(filtros);
        if (idEstrutura !== null) {
            request.idEstruturaOrganizacional = idEstrutura;
        }

        return request;
    }

    private executarAcaoTabelaQueryPendente(): void {
        const acao = this.acaoTabelaQueryPendente;
        if (!acao) {
            return;
        }

        const codigoReuniao = presenter.parseNumeroPositivo(this.filtrosAtivos.codigoReuniao);
        const reuniao = codigoReuniao !== null ? this.reunioesFiltradas.find((item) => presenter.parseNumeroPositivo(item.codigo) === codigoReuniao) : this.reunioesFiltradas[0];

        if (!reuniao) {
            return;
        }

        this.acaoTabelaQueryPendente = null;
        this.reuniaoSelecionada = reuniao;
        this.aoExecutarAcaoTabela(acao);
    }

    private normalizarAcaoTabelaQuery(valor: unknown): AcaoTabelaComissoes | null {
        if (typeof valor !== 'string') {
            return null;
        }

        const acoesPermitidas: AcaoTabelaComissoes[] = [
            'abrir-ata',
            'editar',
            'cancelar',
            'excluir',
            'convocacoes',
            'ir-para-atividades',
            'visualizar-comissao-nucleo',
            'nova-atividade-reuniao',
            'reativar-reuniao',
            'status-ata-anterior',
            'status-ata-proximo',
            'visualizar-selecionada'
        ];

        return acoesPermitidas.includes(valor as AcaoTabelaComissoes) ? (valor as AcaoTabelaComissoes) : null;
    }

    private mapearIdTipoComissaoCt(tipoComissao: FiltrosComissoes['tipoComissao']): number | null {
        const mapa: Record<Exclude<FiltrosComissoes['tipoComissao'], 'todas'>, number> = {
            'comissao-interna': 1,
            'camara-tecnica-interna': 2,
            'comissao-camara-externa': 3,
            'grupo-trabalho-interno': 4
        };

        if (tipoComissao === 'todas') {
            return null;
        }

        return mapa[tipoComissao];
    }

    private mapearIdStatusReuniao(status: FiltrosComissoes['status']): number | null {
        const mapa: Record<Exclude<FiltrosComissoes['status'], 'todas'>, number> = {
            agendada: 1,
            remarcada: 2,
            cancelada: 3,
            realizada: 4,
            excluida: 5
        };

        if (status === 'todas') {
            return null;
        }

        return mapa[status];
    }

    private mapearTipoPeriodo(periodo: PeriodoFiltroComissao): EComissaoReuniaoTipoPeriodoFiltro {
        const mapa: Record<PeriodoFiltroComissao, EComissaoReuniaoTipoPeriodoFiltro> = {
            todos: EComissaoReuniaoTipoPeriodoFiltro.TODOS,
            'por-periodo': EComissaoReuniaoTipoPeriodoFiltro.POR_PERIODO,
            'codigo-reuniao': EComissaoReuniaoTipoPeriodoFiltro.CODIGO_REUNIAO,
            'proximos-15-dias': EComissaoReuniaoTipoPeriodoFiltro.PROXIMOS_15_DIAS,
            'proximos-30-dias': EComissaoReuniaoTipoPeriodoFiltro.PROXIMOS_30_DIAS
        };
        return mapa[periodo];
    }

    private mapearReuniao(item: ComissaoReuniaoResponse): ReuniaoComissao {
        const totalMembros = presenter.numeroSeguro(item.totalMembros);
        const totalConvocacoes = presenter.numeroSeguro(item.totalConvocacoes);
        const totalConfirmacoes = presenter.numeroSeguro(item.totalConfirmacoes);

        return {
            codigo: String(item.idReuniao ?? ''),
            tipoPessoa: 'acompanhamento',
            status: this.mapearStatusReuniao(item.dsStatusReuniao),
            idStatusReuniao: item.idStatusReuniao ?? undefined,
            descricaoStatus: item.dsStatusReuniao ?? undefined,
            visivelPortal: presenter.normalizarTexto(item.inVisivelPortal) === 'S',
            modalidade: this.mapearModalidade(item.inModalidade, item.dsModalidade),
            atividades: this.mapearAtividades(item.possuiAtividades),
            codigoComissao: String(item.idEstruturaOrganizacional ?? '-'),
            idEstruturaOrganizacional: item.idEstruturaOrganizacional ?? undefined,
            nomeComissao: item.dsEstruturaOrganizacional || '-',
            statusAta: item.dsStatusAta || '',
            idStatusAta: item.idStatusAta ?? undefined,
            descricaoStatusAta: item.dsStatusAta ?? undefined,
            idProximoStatus: item.idProximoStatus ?? undefined,
            idStatusAnterior: item.idStatusAnterior ?? undefined,
            descricaoStatusAtaProximo: item.dsStatusAtaProximo ?? undefined,
            descricaoStatusAtaAnterior: item.dsStatusAtaAnterior ?? undefined,
            local: item.dsLocalReuniao || '-',
            idLocalReuniao: item.idLocalReuniao ?? undefined,
            dataHoraCadastro: this.converterData(item.dtUltimaAlteracao),
            dataInicio: this.converterData(item.dhInicioReuniao),
            dataTermino: this.converterData(item.dhFimReuniao),
            convocacoes: totalConvocacoes,
            confirmacoes: totalConfirmacoes,
            totalMembros,
            atosConcessao: presenter.numeroSeguro(item.totalAtos),
            importante: item.inImportante || '',
            dsImportante: item.dsImportante ?? undefined,
            protocolo: item.numeroProtocolo || '',
            assistente: item.nmSolicitanteAssistente || '-',
            idSolicitanteAssistente: item.idSolicitanteAssistente ?? undefined,
            ufSolicitanteAssistente: item.ufSolicitanteAssistente ?? undefined,
            idSolicitanteCoordenador: item.idSolicitanteCoordenador ?? undefined,
            ufSolicitanteCoordenador: item.sgUfCoordenador ?? undefined,
            nomeCoordenador: item.nmSolicitanteCoordenador ?? undefined,
            membrosConvidadosPresentes: [],
            tipoComissao: this.mapearTipoComissao(item.idTipoComissaoCt, item.dsEstruturaOrganizacional),
            idTipoComissaoCt: item.idTipoComissaoCt ?? undefined,
            possuiConvocacaoPendente: totalMembros > 0 && totalConvocacoes < totalMembros,
            possuiConfirmacaoPendente: totalMembros > 0 && totalConfirmacoes < totalMembros,
            ataMarcada: presenter.normalizarTexto(item.inImportante) === 'S'
        };
    }

    private aplicarFiltrosComplementares(reunioes: ReuniaoComissao[], filtros: FiltrosComissoes): ReuniaoComissao[] {
        return reunioes.filter((reuniao) => {
            if (!this.filtroPeriodoValido(reuniao, filtros)) {
                return false;
            }

            if (filtros.codigoReuniao && !reuniao.codigo.includes(filtros.codigoReuniao.trim())) {
                return false;
            }

            if (filtros.status !== 'todas' && reuniao.status !== filtros.status) {
                return false;
            }

            if (filtros.tipoComissao !== 'todas' && reuniao.tipoComissao !== filtros.tipoComissao) {
                return false;
            }

            if (filtros.comissaoEstruturaId !== null) {
                if (reuniao.idEstruturaOrganizacional !== filtros.comissaoEstruturaId) {
                    return false;
                }
            } else if (filtros.comissaoNome) {
                const buscaComissao = filtros.comissaoNome.trim().toLowerCase();
                if (!reuniao.nomeComissao.toLowerCase().includes(buscaComissao) && !reuniao.codigoComissao.includes(buscaComissao)) {
                    return false;
                }
            }

            if (filtros.membroConvidado && reuniao.membrosConvidadosPresentes.length > 0) {
                const buscaMembro = filtros.membroConvidado.trim().toLowerCase();
                const contemMembro = reuniao.membrosConvidadosPresentes.some((nome) => nome.toLowerCase().includes(buscaMembro));
                if (!contemMembro) {
                    return false;
                }
            }

            if (filtros.assistente !== 'todos') {
                const filtroAssistente = filtros.assistente;
                const idUfAssistente = this.parseIdUfDoFiltro(filtroAssistente);
                if (idUfAssistente) {
                    if (reuniao.idSolicitanteAssistente !== idUfAssistente.id || presenter.normalizarTexto(reuniao.ufSolicitanteAssistente) !== idUfAssistente.uf) {
                        return false;
                    }
                } else {
                    const nomeAssistenteFiltro = this.removerPrefixoTexto(filtroAssistente).toLowerCase();
                    if (!reuniao.assistente.toLowerCase().includes(nomeAssistenteFiltro)) {
                        return false;
                    }
                }
            }

            if (filtros.statusAta !== 'todos') {
                const idStatusAta = this.parseIdStatusAtaFiltro(filtros.statusAta);
                if (idStatusAta !== null) {
                    if (reuniao.idStatusAta !== idStatusAta) {
                        return false;
                    }
                } else {
                    const statusAtaMapeado = this.mapearStatusAtaParaFiltro(reuniao.statusAta);
                    if (statusAtaMapeado !== filtros.statusAta) {
                        return false;
                    }
                }
            }

            if (filtros.local !== 'todos') {
                const idLocalFiltro = this.parseIdDoFiltro(filtros.local);
                if (idLocalFiltro !== null) {
                    if (reuniao.idLocalReuniao !== idLocalFiltro) {
                        return false;
                    }
                } else {
                    const nomeLocalFiltro = this.removerPrefixoTexto(filtros.local).toLowerCase();
                    if (!reuniao.local.toLowerCase().includes(nomeLocalFiltro)) {
                        return false;
                    }
                }
            }

            if (filtros.convocacoes === 'pendentes' && !reuniao.possuiConvocacaoPendente) {
                return false;
            }

            if (filtros.convocacoes === 'confirmacoes-pendentes' && !reuniao.possuiConfirmacaoPendente) {
                return false;
            }

            if (filtros.atas === 'nao-marcadas' && reuniao.ataMarcada) {
                return false;
            }

            if (filtros.atas === 'importantes' && !reuniao.ataMarcada) {
                return false;
            }

            return true;
        });
    }

    private filtroPeriodoValido(reuniao: ReuniaoComissao, filtros: FiltrosComissoes): boolean {
        if (filtros.periodo === 'todos' || filtros.periodo === 'codigo-reuniao') {
            return true;
        }

        if (!reuniao.dataInicio) {
            return false;
        }

        if (filtros.periodo === 'por-periodo') {
            if (!filtros.dataInicial && !filtros.dataFinal) {
                return true;
            }

            const dataReuniao = reuniao.dataInicio.getTime();
            const inicio = filtros.dataInicial ? this.inicioDia(new Date(`${filtros.dataInicial}T00:00:00`)).getTime() : Number.MIN_SAFE_INTEGER;
            const fim = filtros.dataFinal ? this.fimDia(new Date(`${filtros.dataFinal}T00:00:00`)).getTime() : Number.MAX_SAFE_INTEGER;
            return dataReuniao >= inicio && dataReuniao <= fim;
        }

        const limiteDias = filtros.periodo === 'proximos-15-dias' ? 15 : 30;
        const hoje = this.inicioDia(new Date());
        const limite = this.fimDia(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + limiteDias));

        return reuniao.dataInicio >= hoje && reuniao.dataInicio <= limite;
    }

    private inicioDia(data: Date): Date {
        const resultado = new Date(data);
        resultado.setHours(0, 0, 0, 0);
        return resultado;
    }

    private fimDia(data: Date): Date {
        const resultado = new Date(data);
        resultado.setHours(23, 59, 59, 999);
        return resultado;
    }

    private mapearStatusReuniao(descricaoStatus?: string): ReuniaoComissao['status'] {
        const status = presenter.normalizarTexto(descricaoStatus);
        if (status.includes('REMARC')) {
            return 'remarcada';
        }
        if (status.includes('REALIZ')) {
            return 'realizada';
        }
        if (status.includes('CANCEL')) {
            return 'cancelada';
        }
        if (status.includes('EXCLUI')) {
            return 'excluida';
        }
        return 'agendada';
    }

    private mapearStatusAtaParaFiltro(descricaoStatusAta?: string): Exclude<FiltrosComissoes['statusAta'], 'todos' | `id:${number}`> {
        const statusAta = presenter.normalizarTexto(descricaoStatusAta);
        if (statusAta.includes('REALIZ')) {
            return 'realizada';
        }
        if (statusAta.includes('CANCEL')) {
            return 'cancelada';
        }
        return 'pendente';
    }

    private mapearTipoComissao(idTipoComissaoCt?: number, descricaoEstrutura?: string): ReuniaoComissao['tipoComissao'] {
        if (idTipoComissaoCt === 1) {
            return 'comissao-interna';
        }

        if (idTipoComissaoCt === 2) {
            return 'camara-tecnica-interna';
        }

        if (idTipoComissaoCt === 3) {
            return 'comissao-camara-externa';
        }

        if (idTipoComissaoCt === 4) {
            return 'grupo-trabalho-interno';
        }

        const descricao = presenter.normalizarTexto(descricaoEstrutura);

        if (descricao.includes('GRUPO')) {
            return 'grupo-trabalho-interno';
        }

        if (descricao.includes('CAMARA')) {
            return 'camara-tecnica-interna';
        }

        if (descricao.includes('EXTERNA') || descricao.includes('EXTERNO')) {
            return 'comissao-camara-externa';
        }

        return 'comissao-interna';
    }

    private mapearModalidade(inModalidade?: string, descricaoModalidade?: string): string {
        const descricao = presenter.normalizarTexto(descricaoModalidade);
        if (descricao.includes('HIBRID')) {
            return 'H\u00edbrida';
        }
        if (descricao.includes('PRESENC')) {
            return 'Presencial';
        }
        if (descricao.includes('NAO INFORM') || descricao.includes('VIRTUAL')) {
            return 'N\u00e3o Informada';
        }

        const modalidade = presenter.normalizarTexto(inModalidade);
        if (modalidade === 'H') {
            return 'H\u00edbrida';
        }
        if (modalidade === 'P') {
            return 'Presencial';
        }
        return 'N\u00e3o Informada';
    }

    private mapearAtividades(possuiAtividades?: number): string {
        return possuiAtividades && possuiAtividades > 0 ? 'Sim' : 'Não';
    }

    private parseIdEstruturaFiltro(filtros: FiltrosComissoes): number | null {
        if (typeof filtros.comissaoEstruturaId === 'number' && Number.isFinite(filtros.comissaoEstruturaId) && filtros.comissaoEstruturaId > 0) {
            return Math.trunc(filtros.comissaoEstruturaId);
        }

        const match = filtros.comissaoNome.trim().match(/^(\d+)\s*-/);
        if (match) {
            const numero = Number(match[1]);
            if (Number.isFinite(numero) && numero > 0) {
                return Math.trunc(numero);
            }
        }

        return presenter.parseNumeroPositivo(filtros.comissaoNome);
    }

    private parseIdDoFiltro(valorFiltro?: string | null): number | null {
        if (!valorFiltro || !valorFiltro.startsWith('id:')) {
            return null;
        }

        const numero = Number(valorFiltro.replace('id:', '').trim());
        if (!Number.isFinite(numero) || numero <= 0) {
            return null;
        }

        return Math.trunc(numero);
    }

    private parseIdStatusAtaFiltro(statusAta: FiltrosComissoes['statusAta']): number | null {
        return this.parseIdDoFiltro(statusAta);
    }

    private parseIdUfDoFiltro(valorFiltro?: string | null): { id: number; uf: string } | null {
        if (!valorFiltro || !valorFiltro.startsWith('id:') || !valorFiltro.includes('|uf:')) {
            return null;
        }

        const [parteId, parteUf] = valorFiltro.split('|uf:');
        const id = Number(parteId.replace('id:', '').trim());
        const uf = presenter.normalizarTexto(parteUf);

        if (!Number.isFinite(id) || id <= 0 || uf.length !== 2) {
            return null;
        }

        return { id: Math.trunc(id), uf };
    }

    private parseIdUfDoTexto(valorTexto?: string | null): { id: number; uf: string } | null {
        if (!valorTexto) {
            return null;
        }

        const match = valorTexto.trim().match(/^(\d+)\s*[/|-]\s*([A-Za-z]{2})$/);

        if (!match) {
            return null;
        }

        const id = Number(match[1]);
        const uf = presenter.normalizarTexto(match[2]);

        if (!Number.isFinite(id) || id <= 0 || uf.length !== 2) {
            return null;
        }

        return { id: Math.trunc(id), uf };
    }

    private removerPrefixoTexto(valorFiltro: string): string {
        if (valorFiltro.startsWith('txt:')) {
            return valorFiltro.slice(4);
        }
        return valorFiltro;
    }

    private converterData(valorData?: string): Date | null {
        if (!valorData) {
            return null;
        }
        const data = new Date(valorData);
        return Number.isNaN(data.getTime()) ? null : data;
    }

    private paginaAtual(): number {
        return Math.trunc(this.primeiraLinhaReunioes / this.linhasPorPaginaReunioes);
    }

    private reiniciarPaginacaoReunioes(): void {
        this.primeiraLinhaReunioes = 0;
        this.linhasPorPaginaReunioes = 20;
    }

    private carregarStatusAta(): void {
        this.consultaStatusAtaSub?.unsubscribe();
        this.consultaStatusAtaSub = this.comissaoReuniaoService.listarStatusAta().subscribe({
            next: (statusAta) => {
                this.opcoesStatusAta = this.mapearOpcoesStatusAta(statusAta);
                this.cdr.markForCheck();
            },
            error: () => {
                this.opcoesStatusAta = [{ rotulo: 'Todos', valor: 'todos' }];
                this.cdr.markForCheck();
            }
        });
    }

    private carregarLocaisReuniao(): void {
        this.consultaLocaisSub?.unsubscribe();
        this.consultaLocaisSub = this.comissaoReuniaoService.listarLocaisReuniao().subscribe({
            next: (locais) => {
                this.locaisReuniao = locais ?? [];
                this.opcoesLocais = presenter.mapearOpcoesLocais(locais);
                this.cdr.markForCheck();
            },
            error: () => {
                this.locaisReuniao = [];
                this.opcoesLocais = [{ rotulo: 'Todos', valor: 'todos' }];
                this.cdr.markForCheck();
            }
        });
    }

    private carregarAssistentes(): void {
        this.consultaAssistentesSub?.unsubscribe();
        this.consultaAssistentesSub = this.comissaoReuniaoService.listarAssistentes(ID_ESTRUTURA_TODAS_COMISSOES).subscribe({
            next: (assistentes) => {
                this.opcoesAssistentes = presenter.mapearOpcoesAssistentes(assistentes);
                this.cdr.markForCheck();
            },
            error: () => {
                this.opcoesAssistentes = [{ rotulo: 'TODOS', valor: 'todos' }];
                this.cdr.markForCheck();
            }
        });
    }

    private mapearOpcoesStatusAta(statusAta: ComissaoStatusAtaResponse[]): OpcaoFiltroComissao[] {
        const opcoes = statusAta
            .map((item) => {
                const id = presenter.numeroSeguro(item.idStatusAta);
                const descricao = (item.dsStatusAta ?? '').trim();

                if (id <= 0) {
                    return { rotulo: descricao || 'Todos', valor: 'todos' };
                }

                return {
                    rotulo: descricao || `Status ${id}`,
                    valor: `id:${id}`
                };
            })
            .filter((item) => item.rotulo)
            .sort((a, b) => {
                if (a.valor === 'todos') {
                    return -1;
                }
                if (b.valor === 'todos') {
                    return 1;
                }
                return a.rotulo.localeCompare(b.rotulo);
            });

        if (!opcoes.some((item) => item.valor === 'todos')) {
            return [{ rotulo: 'Todos', valor: 'todos' }, ...opcoes];
        }

        return opcoes;
    }

    private reiniciarEstadoAcompanhamento(): void {
        this.consultaReunioesSub?.unsubscribe();
        this.consultaDashboardSummarySub?.unsubscribe();
        this.consultaStatusAtaSub?.unsubscribe();
        this.consultaLocaisSub?.unsubscribe();
        this.consultaAssistentesSub?.unsubscribe();
        this.consultaDocumentoAtaSub?.unsubscribe();
        this.consultaMembrosAtaSub?.unsubscribe();
        this.consultaConvidadosAtaSub?.unsubscribe();
        this.consultaAtividadesAtaSub?.unsubscribe();
        this.dashboardSummary = null;
        this.reunioesPorTipo = [];
        this.reunioesFiltradas = [];
        this.totalRegistrosReunioes = 0;
        this.reiniciarPaginacaoReunioes();
        this.opcoesLocais = [{ rotulo: 'Todos', valor: 'todos' }];
        this.opcoesAssistentes = [{ rotulo: 'TODOS', valor: 'todos' }];
        this.opcoesStatusAta = [{ rotulo: 'Todos', valor: 'todos' }];
        this.tiposComissao = [];
        this.estruturasComissao = [];
        this.carregandoEstruturasComissao = false;
        this.erroEstruturasComissao = '';
        this.erroConsulta = '';
        this.carregandoReunioes = false;
        this.telaAtaVisivel = false;
        this.limparEstadoDocumentoAta();
    }

    private abrirAtaReuniao(reuniao: ReuniaoComissao): void {
        this.telaAtaVisivel = true;
        this.limparEstadoDocumentoAta();

        const idReuniao = presenter.parseNumeroPositivo(reuniao.codigo);
        if (idReuniao === null || reuniao.protocolo?.trim()) {
            this.carregarDadosAtaReuniao(reuniao);
            return;
        }

        this.idReuniaoProtocoloAtaConsulta = idReuniao;
        this.gerarProtocoloAtaSub = this.garantirCoordenadorAtaAntesProtocolo(idReuniao, reuniao)
            .pipe(switchMap((possuiCoordenador) => (possuiCoordenador ? this.comissaoReuniaoService.gerarProtocoloAta(idReuniao) : of({} as ComissaoAtaProtocoloResponse))))
            .subscribe({
                next: (response) => {
                    if (this.idReuniaoProtocoloAtaConsulta !== idReuniao) {
                        return;
                    }

                    const reuniaoAtualizada = this.aplicarProtocoloGeradoAta(reuniao, response);
                    this.carregarDadosAtaReuniao(reuniaoAtualizada);
                    this.cdr.markForCheck();
                },
                error: () => {
                    if (this.idReuniaoProtocoloAtaConsulta !== idReuniao) {
                        return;
                    }

                    this.ultimaAcaoExecutada = 'Não foi possível gerar o protocolo da ata.';
                    this.carregarDadosAtaReuniao(reuniao);
                    this.cdr.markForCheck();
                }
            });
    }

    private garantirCoordenadorAtaAntesProtocolo(idReuniao: number, reuniao: ReuniaoComissao): Observable<boolean> {
        return this.obterCoordenadorComposicaoAtual(reuniao).pipe(
            switchMap((coordenadorComposicao) => {
                if (!coordenadorComposicao) {
                    return of(false);
                }

                return this.garantirCoordenadorRegistroReuniao(idReuniao, reuniao, coordenadorComposicao).pipe(
                    catchError(() => of(void 0)),
                    map(() => true)
                );
            })
        );
    }

    private garantirCoordenadorRegistroReuniao(idReuniao: number, reuniao: ReuniaoComissao, coordenador: IdentificacaoParticipanteAta): Observable<void> {
        const coordenadorAtual = this.obterCoordenadorReuniao(reuniao);
        if (coordenadorAtual && this.ehMesmoParticipante(coordenadorAtual.idSolicitante, coordenadorAtual.uf, coordenador)) {
            return of(void 0);
        }

        const request = this.criarPayloadAtualizacaoReuniao(reuniao, coordenador);
        if (!request) {
            return of(void 0);
        }

        return this.comissaoReuniaoService.atualizarReuniao(idReuniao, request).pipe(
            map(() => {
                this.aplicarCoordenadorEstadoReuniao(idReuniao, coordenador);
                return void 0;
            })
        );
    }

    private criarPayloadAtualizacaoReuniao(reuniao: ReuniaoComissao, coordenador: IdentificacaoParticipanteAta): ComissaoReuniaoSalvarRequest | null {
        const dhInicioReuniao = this.formatarDataHoraRequest(reuniao.dataInicio);
        const dhFimReuniao = this.formatarDataHoraRequest(reuniao.dataTermino);
        const idLocalReuniao = presenter.numeroSeguro(reuniao.idLocalReuniao);
        const idEstruturaOrganizacional = presenter.numeroSeguro(reuniao.idEstruturaOrganizacional);
        const idTipoComissaoCt = presenter.numeroSeguro(reuniao.idTipoComissaoCt);

        if (!dhInicioReuniao || !dhFimReuniao || idLocalReuniao <= 0 || idEstruturaOrganizacional <= 0 || idTipoComissaoCt <= 0) {
            return null;
        }

        return {
            dhInicioReuniao,
            dhFimReuniao,
            idLocalReuniao,
            idEstruturaOrganizacional,
            idTipoComissaoCt,
            idSolicitanteAssistente: reuniao.idSolicitanteAssistente ?? null,
            ufSolicitanteAssistente: reuniao.ufSolicitanteAssistente ?? null,
            idSolicitanteCoordenador: coordenador.idSolicitante,
            sgUfCoordenador: coordenador.uf,
            inModalidade: this.mapearModalidadeRequest(reuniao.modalidade),
            inVisivelPortal: reuniao.visivelPortal ? 'S' : 'N'
        };
    }

    private aplicarCoordenadorEstadoReuniao(idReuniao: number, coordenador: IdentificacaoParticipanteAta): void {
        const codigo = String(idReuniao);
        const aplicar = (item: ReuniaoComissao): ReuniaoComissao => {
            if (item.codigo !== codigo) {
                return item;
            }

            return {
                ...item,
                idSolicitanteCoordenador: coordenador.idSolicitante,
                ufSolicitanteCoordenador: coordenador.uf
            };
        };

        this.reunioesPorTipo = this.reunioesPorTipo.map(aplicar);
        this.reunioesFiltradas = this.reunioesFiltradas.map(aplicar);

        if (this.reuniaoSelecionada?.codigo === codigo) {
            this.reuniaoSelecionada = aplicar(this.reuniaoSelecionada);
        }
    }

    private formatarDataHoraRequest(data?: Date | null): string {
        if (!data || Number.isNaN(data.getTime())) {
            return '';
        }

        return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}T${String(data.getHours()).padStart(2, '0')}:${String(data.getMinutes()).padStart(2, '0')}:${String(data.getSeconds()).padStart(2, '0')}`;
    }

    private mapearModalidadeRequest(modalidade?: string | null): string {
        const valor = this.normalizarTextoSemAcento(modalidade);
        if (valor.includes('PRESENC')) {
            return 'P';
        }
        if (valor.includes('VIRTUAL')) {
            return 'R';
        }
        return 'H';
    }

    private obterCoordenadorComposicaoAtual(reuniao: ReuniaoComissao): Observable<IdentificacaoParticipanteAta | null> {
        const idEstrutura = presenter.numeroSeguro(reuniao.idEstruturaOrganizacional);
        if (idEstrutura <= 0) {
            return of(null);
        }

        return this.comissaoCadastroService.listarMembrosAtuais(idEstrutura).pipe(
            map((membros) => this.encontrarCoordenadorComposicao(membros ?? [])),
            catchError(() => of(null))
        );
    }

    private encontrarCoordenadorComposicao(membros: CctMembroResponse[]): IdentificacaoParticipanteAta | null {
        const coordenador =
            membros.find((membro) => Number(membro.idParticipacao ?? 0) === 1) ??
            membros.find((membro) => {
                const participacao = this.normalizarTextoSemAcento(membro.dsParticipacao);
                return participacao.includes('COORDENADOR') && !participacao.includes('VICE');
            });

        return coordenador ? this.criarIdentificacaoParticipante(coordenador.idSolicitante, coordenador.uf) : null;
    }

    private carregarDadosAtaReuniao(reuniao: ReuniaoComissao): void {
        this.carregarModelosAta();
        this.carregarAutosaveAta(reuniao);
        this.carregarDocumentoAtaReuniao(reuniao);
        this.carregarMembrosAtaReuniao(reuniao);
        this.carregarConvidadosAtaReuniao(reuniao);
        this.carregarAtividadesAtaReuniao(reuniao);
    }

    private aplicarProtocoloGeradoAta(reuniao: ReuniaoComissao, response: ComissaoAtaProtocoloResponse): ReuniaoComissao {
        const protocolo = (response.numeroProtocolo ?? '').trim();
        if (!protocolo) {
            return reuniao;
        }

        const reuniaoAtualizada = { ...reuniao, protocolo };
        if (this.reuniaoSelecionada?.codigo === reuniao.codigo) {
            this.reuniaoSelecionada = reuniaoAtualizada;
        }

        this.reunioesPorTipo = this.atualizarProtocoloReuniao(this.reunioesPorTipo, reuniao.codigo, protocolo);
        this.reunioesFiltradas = this.atualizarProtocoloReuniao(this.reunioesFiltradas, reuniao.codigo, protocolo);
        return reuniaoAtualizada;
    }

    private atualizarProtocoloReuniao(reunioes: ReuniaoComissao[], codigo: string, protocolo: string): ReuniaoComissao[] {
        return reunioes.map((item) => (item.codigo === codigo ? { ...item, protocolo } : item));
    }

    private carregarDocumentoAtaReuniao(reuniao: ReuniaoComissao): void {
        const idReuniao = presenter.parseNumeroPositivo(reuniao.codigo);

        this.consultaDocumentoAtaSub?.unsubscribe();
        this.documentoAtaHtml = '';

        if (idReuniao === null) {
            return;
        }

        this.idReuniaoDocumentoAtaConsulta = idReuniao;

        this.consultaDocumentoAtaSub = this.comissaoReuniaoService.consultarDocumentoAtaHtml(idReuniao).subscribe({
            next: (response) => {
                if (this.idReuniaoDocumentoAtaConsulta !== idReuniao) {
                    return;
                }

                this.documentoAtaHtml = (response.txDocumentoHtml ?? '').trim();
                this.etagAtaAtual = response.etag ?? '';
                this.autosavePausadoAta = false;
                this.conflitoAtaMensagem = '';
                this.cdr.markForCheck();
            },
            error: () => {
                if (this.idReuniaoDocumentoAtaConsulta !== idReuniao) {
                    return;
                }

                this.documentoAtaHtml = '';
                this.etagAtaAtual = '';
                this.cdr.markForCheck();
            }
        });
    }

    private carregarModelosAta(): void {
        this.modelosAta = [];

        this.consultaModelosAtaSub?.unsubscribe();
        // Chamada desativada temporariamente: endpoint consulta a tabela SCCT_MODELO_ATA.
        /*
        this.consultaModelosAtaSub = this.comissaoReuniaoService.listarModelosAta().subscribe({
            next: (response) => {
                this.modelosAta = response ?? [];
            },
            error: () => {
                this.modelosAta = [];
                this.ultimaAcaoExecutada = 'Não foi possível carregar os modelos de ata.';
            }
        });
        */
    }

    private carregarAutosaveAta(reuniao: ReuniaoComissao): void {
        const idEstruturaOrganizacional = Number(reuniao.idEstruturaOrganizacional);
        this.consultaAutosaveAtaSub?.unsubscribe();
        this.autosaveIntervaloSegundos = 0;

        if (!Number.isFinite(idEstruturaOrganizacional) || idEstruturaOrganizacional <= 0) {
            return;
        }

        this.consultaAutosaveAtaSub = this.comissaoReuniaoService.consultarAutosaveAta(Math.trunc(idEstruturaOrganizacional)).subscribe({
            next: (response) => {
                this.autosaveIntervaloSegundos = response.habilitado ? presenter.numeroSeguro(response.intervaloSegundos) : 0;
                this.cdr.markForCheck();
            },
            error: () => {
                this.autosaveIntervaloSegundos = 0;
                this.cdr.markForCheck();
            }
        });
    }

    private carregarTextoInicioAta(reuniao: ReuniaoComissao): void {
        const idReuniao = presenter.parseNumeroPositivo(reuniao.codigo);

        this.consultaTextoInicioAtaSub?.unsubscribe();

        if (idReuniao === null) {
            return;
        }

        this.idReuniaoTextoInicioAtaConsulta = idReuniao;

        this.consultaTextoInicioAtaSub = this.comissaoReuniaoService.gerarTextoInicioAta(idReuniao).subscribe({
            next: (response) => {
                if (this.idReuniaoTextoInicioAtaConsulta !== idReuniao) {
                    return;
                }

                this.aplicarTextoInicioAta(response);
                this.cdr.markForCheck();
            },
            error: () => {
                if (this.idReuniaoTextoInicioAtaConsulta !== idReuniao) {
                    return;
                }

                this.textoInicioAtaModelo = '';
                this.cdr.markForCheck();
            }
        });
    }

    private carregarTextoFinalizacaoAta(reuniao: ReuniaoComissao): void {
        const idReuniao = presenter.parseNumeroPositivo(reuniao.codigo);

        this.consultaTextoFinalizacaoAtaSub?.unsubscribe();

        if (idReuniao === null) {
            return;
        }

        this.idReuniaoTextoFinalizacaoAtaConsulta = idReuniao;

        this.consultaTextoFinalizacaoAtaSub = this.comissaoReuniaoService.gerarTextoFinalizacaoAta(idReuniao).subscribe({
            next: (response) => {
                if (this.idReuniaoTextoFinalizacaoAtaConsulta !== idReuniao) {
                    return;
                }

                this.aplicarTextoFinalizacaoAta(response);
                this.cdr.markForCheck();
            },
            error: () => {
                if (this.idReuniaoTextoFinalizacaoAtaConsulta !== idReuniao) {
                    return;
                }

                this.textoFinalizacaoAtaModelo = '';
                this.cdr.markForCheck();
            }
        });
    }

    private carregarMembrosAtaReuniao(reuniao: ReuniaoComissao): void {
        const idReuniao = presenter.parseNumeroPositivo(reuniao.codigo);

        this.consultaMembrosAtaSub?.unsubscribe();
        this.membrosAta = [];

        if (idReuniao === null) {
            return;
        }

        this.idReuniaoMembrosAtaConsulta = idReuniao;

        this.consultaMembrosAtaSub = this.comissaoReuniaoService.listarMembrosReuniao(idReuniao).subscribe({
            next: (response) => {
                if (this.idReuniaoMembrosAtaConsulta !== idReuniao) {
                    return;
                }

                this.membrosAta = this.mapearMembrosAta(response ?? []);
                this.cdr.markForCheck();
            },
            error: () => {
                if (this.idReuniaoMembrosAtaConsulta !== idReuniao) {
                    return;
                }

                this.membrosAta = [];
                this.cdr.markForCheck();
            }
        });
    }

    private carregarConvidadosAtaReuniao(reuniao: ReuniaoComissao): void {
        const idReuniao = presenter.parseNumeroPositivo(reuniao.codigo);

        this.consultaConvidadosAtaSub?.unsubscribe();
        this.convidadosAta = [];

        if (idReuniao === null) {
            return;
        }

        this.idReuniaoConvidadosAtaConsulta = idReuniao;

        this.consultaConvidadosAtaSub = this.comissaoReuniaoService.listarConvidadosAta(idReuniao).subscribe({
            next: (response) => {
                if (this.idReuniaoConvidadosAtaConsulta !== idReuniao) {
                    return;
                }

                this.convidadosAta = this.mapearConvidadosAta(response ?? []);
                this.cdr.markForCheck();
            },
            error: () => {
                if (this.idReuniaoConvidadosAtaConsulta !== idReuniao) {
                    return;
                }

                this.convidadosAta = [];
                this.cdr.markForCheck();
            }
        });
    }

    private carregarAtividadesAtaReuniao(reuniao: ReuniaoComissao): void {
        const idReuniao = presenter.parseNumeroPositivo(reuniao.codigo);

        this.consultaAtividadesAtaSub?.unsubscribe();
        this.atividadesAta = [];

        if (idReuniao === null) {
            return;
        }

        this.consultaAtividadesAtaSub = this.comissaoAtividadeService.consultarAtividades({ tipoPesquisa: EComissaoAtividadeTipoPesquisaFiltro.CODIGO_REUNIAO, codigoPesquisa: idReuniao }, 0, 200).subscribe({
            next: (response) => {
                this.atividadesAta = this.mapearAtividadesAta(response.content ?? []);
                this.cdr.markForCheck();
            },
            error: () => {
                this.atividadesAta = [];
                this.cdr.markForCheck();
            }
        });
    }

    private mapearMembrosAta(membros: ComissaoReuniaoMembroResponse[]): ParticipanteAta[] {
        return membros.map((membro) => {
            const inFuncao = (membro.inFuncao ?? '').trim() || undefined;

            return {
                idSolicitante: presenter.numeroSeguro(membro.idSolicitante),
                uf: (membro.ufSgUf ?? '').trim() || '-',
                nome: (membro.nmSolicitante ?? '').trim() || '-',
                inPresenca: membro.inPresenca ?? undefined,
                inFuncao,
                situacao: this.mapearSituacaoMembro(membro.inPresenca, inFuncao)
            };
        });
    }

    private obterCoordenadorReuniao(reuniao?: ReuniaoComissao | null): IdentificacaoParticipanteAta | null {
        if (!reuniao) {
            return null;
        }

        return this.criarIdentificacaoParticipante(reuniao.idSolicitanteCoordenador, reuniao.ufSolicitanteCoordenador);
    }

    private criarIdentificacaoParticipante(idSolicitante?: number | null, uf?: string | null): IdentificacaoParticipanteAta | null {
        const id = presenter.numeroSeguro(idSolicitante);
        const ufNormalizada = presenter.normalizarUf(uf);

        return id > 0 && ufNormalizada ? { idSolicitante: id, uf: ufNormalizada } : null;
    }

    private ehMesmoParticipante(idSolicitante: number | null | undefined, uf: string | null | undefined, participante: IdentificacaoParticipanteAta): boolean {
        return presenter.numeroSeguro(idSolicitante) === participante.idSolicitante && presenter.normalizarUf(uf) === participante.uf;
    }

    private mapearConvidadosAta(convidados: ComissaoAtaConvidadoResponse[]): ParticipanteAta[] {
        return convidados.map((convidado) => ({
            idSolicitante: presenter.numeroSeguro(convidado.idSolicitante),
            uf: (convidado.ufSgUf ?? '').trim() || '-',
            nome: (convidado.nmSolicitante ?? '').trim() || '-',
            inPresenca: convidado.inPresenca ?? undefined,
            inFuncao: convidado.inFuncao ?? undefined,
            situacao: this.mapearSituacaoMembro(convidado.inPresenca, convidado.inFuncao)
        }));
    }

    private mapearAtividadesAta(atividades: ComissaoAtividadeResponse[]): AtividadeAta[] {
        return atividades.map((atividade) => ({
            idTarefa: presenter.numeroSeguro(atividade.idTarefa),
            numero: atividade.numeroTarefa || atividade.codigoTarefa || '-',
            descricao: atividade.dsTarefa || '-',
            resolvida: presenter.numeroSeguro(atividade.idSituacaoTarefa) !== 1,
            txObservacao: atividade.txObservacao,
            dtInicio: atividade.dtInicio,
            dtTermino: atividade.dtTermino,
            idSituacaoTarefa: atividade.idSituacaoTarefa,
            idSolicitanteResponsavel: atividade.idSolicitanteResponsavel,
            sgUfSolicitanteResponsavel: atividade.sgUfSolicitanteResponsavel,
            nmSolicitanteResponsavel: atividade.nmSolicitanteResponsavel,
            idEstruturaOrganizacional: atividade.idEstruturaOrganizacional
        }));
    }

    private aplicarTextoInicioAta(response: ComissaoAtaInicioTextoResponse): void {
        const textoInicioAta = (response.textoInicioAta ?? '').trim();
        if (!textoInicioAta) {
            return;
        }

        this.textoInicioAtaModelo = textoInicioAta;
        this.gatilhoInsercaoInicioAta++;
    }

    private aplicarTextoFinalizacaoAta(response: ComissaoAtaFinalizacaoTextoResponse): void {
        const textoFinalizacaoAta = (response.textoFinalizacaoAta ?? '').trim();
        if (!textoFinalizacaoAta) {
            return;
        }

        this.textoFinalizacaoAtaModelo = textoFinalizacaoAta;
        this.gatilhoInsercaoFinalizacaoAta++;
    }

    private baixarBlobAta(blob: Blob, idReuniao: number): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ata-reuniao-${idReuniao}.rtf`;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    private mapearSituacaoMembro(inPresenca?: string, inFuncao?: string): ParticipanteAta['situacao'] {
        const funcaoNormalizada = this.normalizarTextoSemAcento(inFuncao);

        if (funcaoNormalizada === 'S' || funcaoNormalizada.includes('SECRETARIO')) {
            return 'secretario';
        }

        if (funcaoNormalizada === 'C' || funcaoNormalizada.includes('COORDENADOR')) {
            return 'coordenador';
        }

        const presencaNormalizada = this.normalizarTextoSemAcento(inPresenca);

        if (presencaNormalizada === 'P' || presencaNormalizada.includes('PRESENTE')) {
            return 'presente';
        }

        if (presencaNormalizada === 'V' || presencaNormalizada.includes('VIRTUAL')) {
            return 'virtual';
        }

        if (presencaNormalizada === 'J' || presencaNormalizada.includes('JUSTIFIC')) {
            return 'justificado';
        }

        return 'ausente';
    }

    private obterPresencaGestor(participante: ParticipanteAta): string {
        const presencaNormalizada = this.normalizarTextoSemAcento(participante.inPresenca);
        return presencaNormalizada === 'V' ? 'V' : 'P';
    }

    private limparEstadoDocumentoAta(): void {
        this.consultaDocumentoAtaSub?.unsubscribe();
        this.consultaTextoInicioAtaSub?.unsubscribe();
        this.consultaTextoFinalizacaoAtaSub?.unsubscribe();
        this.consultaMembrosAtaSub?.unsubscribe();
        this.consultaConvidadosAtaSub?.unsubscribe();
        this.consultaAtividadesAtaSub?.unsubscribe();
        this.consultaModelosAtaSub?.unsubscribe();
        this.consultaAutosaveAtaSub?.unsubscribe();
        this.gerarProtocoloAtaSub?.unsubscribe();
        this.autosaveAtaSub?.unsubscribe();
        this.inserirModeloAtaSub?.unsubscribe();
        this.baixarDocumentoAtaSub?.unsubscribe();
        this.idReuniaoDocumentoAtaConsulta = null;
        this.idReuniaoTextoInicioAtaConsulta = null;
        this.idReuniaoTextoFinalizacaoAtaConsulta = null;
        this.idReuniaoMembrosAtaConsulta = null;
        this.idReuniaoConvidadosAtaConsulta = null;
        this.idReuniaoProtocoloAtaConsulta = null;
        this.documentoAtaHtml = '';
        this.textoInicioAtaModelo = '';
        this.gatilhoInsercaoInicioAta = 0;
        this.textoFinalizacaoAtaModelo = '';
        this.gatilhoInsercaoFinalizacaoAta = 0;
        this.membrosAta = [];
        this.convidadosAta = [];
        this.atividadesAta = [];
        this.modelosAta = [];
        this.autosaveIntervaloSegundos = 0;
    }

    private normalizarTextoSemAcento(valor?: string | null): string {
        return presenter
            .normalizarTexto(valor)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    }
}
